/**
 * Silensia Recovery — shared carousel controller.
 *
 * Progressive, dependency-free vanilla JS.
 * CSS scroll-snap handles native paging/swiping.
 *
 * Mobile autoplay behaviour:
 * - Autoplay advances normally.
 * - Touching/swiping pauses immediately.
 * - After releasing, the carousel remains paused for 5 seconds.
 * - Then the normal autoplay interval starts again.
 *
 * Desktop behaviour:
 * - Mouse/manual takeover stops autoplay permanently.
 * - Previous/Next buttons stop autoplay permanently.
 * - Keyboard interaction stops autoplay permanently.
 *
 * Reduced motion:
 * - Autoplay is disabled completely.
 *
 * Lightbox:
 * - Autoplay never advances behind an open dialog.
 */

(function () {
  'use strict';

  function prefersReducedMotion() {
    return (
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function isCoarsePointer() {
    return (
      window.matchMedia &&
      window.matchMedia('(hover: none) and (pointer: coarse)').matches
    );
  }

  /**
   * Mouse drag-to-scroll.
   *
   * Touch devices already use native horizontal scrolling,
   * so this logic only applies to a mouse.
   */
  function initPointerDrag(viewport) {
    var isDown = false;
    var startX = 0;
    var startScrollLeft = 0;

    viewport.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse') return;

      isDown = true;
      startX = e.clientX;
      startScrollLeft = viewport.scrollLeft;

      viewport.style.scrollSnapType = 'none';
      viewport.classList.add('is-dragging');

      viewport.setPointerCapture(e.pointerId);
    });

    viewport.addEventListener('pointermove', function (e) {
      if (!isDown) return;

      viewport.scrollLeft =
        startScrollLeft - (e.clientX - startX);
    });

    function endDrag(e) {
      if (!isDown) return;

      isDown = false;

      viewport.style.scrollSnapType = '';
      viewport.classList.remove('is-dragging');

      if (
        e.pointerId !== undefined &&
        viewport.hasPointerCapture(e.pointerId)
      ) {
        viewport.releasePointerCapture(e.pointerId);
      }
    }

    viewport.addEventListener('pointerup', endDrag);
    viewport.addEventListener('pointercancel', endDrag);
  }

  function initCarousel(root) {
    /**
     * The same JS asset can be included by more than one section.
     * Prevent duplicate listeners/observers.
     */
    if (root.dataset.silensiaCarouselReady === 'true') return;

    root.dataset.silensiaCarouselReady = 'true';

    var viewport = root.querySelector('[data-carousel-viewport]');
    var track = root.querySelector('[data-carousel-track]');

    var prevBtn = root.querySelector('[data-carousel-prev]');
    var nextBtn = root.querySelector('[data-carousel-next]');
    var currentEl = root.querySelector('[data-carousel-current]');

    if (!viewport || !track) return;

    var slides = Array.prototype.slice.call(
      track.querySelectorAll('[data-carousel-slide]')
    );

    if (slides.length < 2) return;

    initPointerDrag(viewport);

    var activeIndex = 0;

    function updateControls() {
      if (prevBtn) {
        prevBtn.disabled = activeIndex === 0;
      }

      if (nextBtn) {
        nextBtn.disabled =
          activeIndex === slides.length - 1;
      }

      if (currentEl) {
        currentEl.textContent =
          String(activeIndex + 1);
      }
    }

    /**
     * Marks the photograph currently being read.
     */
    function markCurrent() {
      for (var i = 0; i < slides.length; i++) {
        slides[i].classList.toggle(
          'is-current',
          i === activeIndex
        );
      }
    }

    function setActiveIndex(index) {
      if (
        index < 0 ||
        index > slides.length - 1 ||
        index === activeIndex
      ) {
        return;
      }

      activeIndex = index;

      updateControls();
      markCurrent();
    }

    /**
     * Move the real viewport so the requested slide
     * is centred in the scrollport.
     */
    function goToIndex(index) {
      var clamped = Math.max(
        0,
        Math.min(index, slides.length - 1)
      );

      var target = slides[clamped];

      var targetRect =
        target.getBoundingClientRect();

      var viewportRect =
        viewport.getBoundingClientRect();

      var delta =
        (
          targetRect.left +
          targetRect.width / 2
        ) -
        (
          viewportRect.left +
          viewportRect.width / 2
        );

      viewport.scrollTo({
        left: viewport.scrollLeft + delta,
        behavior:
          prefersReducedMotion()
            ? 'auto'
            : 'smooth'
      });

      /**
       * Update immediately.
       * IntersectionObserver confirms it once
       * scrolling settles.
       */
      setActiveIndex(clamped);
    }

    if (prevBtn) {
      prevBtn.addEventListener(
        'click',
        function () {
          goToIndex(activeIndex - 1);
        }
      );
    }

    if (nextBtn) {
      nextBtn.addEventListener(
        'click',
        function () {
          goToIndex(activeIndex + 1);
        }
      );
    }

    /**
     * Track whichever slide is most visible.
     */
    if ('IntersectionObserver' in window) {
      var observer =
        new IntersectionObserver(
          function (entries) {
            var best = null;

            entries.forEach(function (entry) {
              if (!entry.isIntersecting) {
                return;
              }

              if (
                !best ||
                entry.intersectionRatio >
                  best.intersectionRatio
              ) {
                best = entry;
              }
            });

            if (
              best &&
              best.intersectionRatio >= 0.6
            ) {
              var index =
                slides.indexOf(best.target);

              if (index !== -1) {
                setActiveIndex(index);
              }
            }
          },
          {
            root: viewport,
            threshold: [0.6]
          }
        );

      slides.forEach(function (slide) {
        observer.observe(slide);
      });
    }

    updateControls();
    markCurrent();

    root.classList.add('is-enhanced');

    /**
     * Optional autoplay.
     */
    initAutoplay(
      root,
      viewport,
      slides,
      prevBtn,
      nextBtn,
      function () {
        return activeIndex;
      },
      goToIndex
    );

    /**
     * Optional lightbox.
     */
    initLightbox(root, viewport);
  }

  /**
   * ---------------------------------------------------------
   * LIGHTBOX
   * ---------------------------------------------------------
   */
  function initLightbox(root, viewport) {
    var host =
      root.closest('section') || document;

    var dialog =
      host.querySelector(
        '[data-carousel-lightbox]'
      );

    var triggers =
      Array.prototype.slice.call(
        root.querySelectorAll(
          '[data-carousel-expand]'
        )
      );

    if (!dialog || !triggers.length) {
      return;
    }

    /**
     * Do not hand-roll dialog behaviour
     * on browsers without native dialog support.
     */
    if (
      typeof dialog.showModal !== 'function'
    ) {
      triggers.forEach(function (trigger) {
        trigger.disabled = true;
        trigger.style.cursor = 'default';
      });

      return;
    }

    var lbViewport =
      dialog.querySelector(
        '[data-carousel-lightbox-viewport]'
      );

    var lbSlides =
      Array.prototype.slice.call(
        dialog.querySelectorAll(
          '[data-carousel-lightbox-slide]'
        )
      );

    var lbCurrent =
      dialog.querySelector(
        '[data-carousel-lightbox-current]'
      );

    var closeBtn =
      dialog.querySelector(
        '[data-carousel-lightbox-close]'
      );

    var opener = null;

    /**
     * Prevent a drag gesture from accidentally
     * opening the lightbox.
     */
    var downX = 0;
    var downY = 0;
    var dragged = false;

    viewport.addEventListener(
      'pointerdown',
      function (e) {
        downX = e.clientX;
        downY = e.clientY;

        dragged = false;
      }
    );

    viewport.addEventListener(
      'pointermove',
      function (e) {
        /**
         * Touch pointermove can report buttons === 0,
         * but horizontal touch swiping should also count
         * as dragging.
         */
        if (
          e.pointerType === 'mouse' &&
          e.buttons === 0
        ) {
          return;
        }

        if (
          Math.abs(e.clientX - downX) > 8 ||
          Math.abs(e.clientY - downY) > 8
        ) {
          dragged = true;
        }
      }
    );

    function showIndex(index) {
      if (
        !lbViewport ||
        !lbSlides.length
      ) {
        return;
      }

      var clamped = Math.max(
        0,
        Math.min(
          index,
          lbSlides.length - 1
        )
      );

      var snap =
        lbViewport.style.scrollSnapType;

      lbViewport.style.scrollSnapType =
        'none';

      /**
       * Force layout after dialog.showModal().
       */
      void lbViewport.offsetWidth;

      lbViewport.scrollLeft =
        lbSlides[clamped].offsetLeft -
        lbSlides[0].offsetLeft;

      lbViewport.style.scrollSnapType =
        snap;

      if (lbCurrent) {
        lbCurrent.textContent =
          String(clamped + 1);
      }
    }

    triggers.forEach(
      function (trigger, index) {
        trigger.addEventListener(
          'click',
          function (e) {
            if (dragged) {
              e.preventDefault();

              dragged = false;
              return;
            }

            opener = trigger;

            dialog.showModal();

            showIndex(index);

            if (closeBtn) {
              closeBtn.focus();
            }
          }
        );
      }
    );

    if (closeBtn) {
      closeBtn.addEventListener(
        'click',
        function () {
          dialog.close();
        }
      );
    }

    /**
     * Tap outside the actual image
     * to close the lightbox.
     */
    function closeIfOutsidePhoto(e) {
      if (e.target.tagName === 'IMG') {
        return;
      }

      dialog.close();
    }

    dialog.addEventListener(
      'click',
      function (e) {
        if (e.target === dialog) {
          dialog.close();
        }
      }
    );

    if (lbViewport) {
      lbViewport.addEventListener(
        'click',
        closeIfOutsidePhoto
      );
    }

    dialog.addEventListener(
      'close',
      function () {
        if (
          opener &&
          document.contains(opener)
        ) {
          opener.focus();
        }

        opener = null;
      }
    );

    /**
     * Lightbox counter.
     */
    if (
      'IntersectionObserver' in window &&
      lbViewport &&
      lbCurrent
    ) {
      var lbObserver =
        new IntersectionObserver(
          function (entries) {
            var best = null;

            entries.forEach(
              function (entry) {
                if (!entry.isIntersecting) {
                  return;
                }

                if (
                  !best ||
                  entry.intersectionRatio >
                    best.intersectionRatio
                ) {
                  best = entry;
                }
              }
            );

            if (
              best &&
              best.intersectionRatio >= 0.6
            ) {
              var index =
                lbSlides.indexOf(
                  best.target
                );

              if (index !== -1) {
                lbCurrent.textContent =
                  String(index + 1);
              }
            }
          },
          {
            root: lbViewport,
            threshold: [0.6]
          }
        );

      lbSlides.forEach(
        function (slide) {
          lbObserver.observe(slide);
        }
      );
    }
  }

  /**
   * ---------------------------------------------------------
   * AUTOPLAY
   * ---------------------------------------------------------
   */
  function initAutoplay(
    root,
    viewport,
    slides,
    prevBtn,
    nextBtn,
    getIndex,
    goToIndex
  ) {
    var interval = parseInt(
      root.getAttribute(
        'data-carousel-autoplay'
      ),
      10
    );

    var toggle =
      root.querySelector(
        '[data-carousel-toggle]'
      );

    /**
     * Autoplay is never enabled for users
     * requesting reduced motion.
     */
    if (
      !interval ||
      interval <= 0 ||
      prefersReducedMotion()
    ) {
      if (toggle) {
        toggle.remove();
      }

      return;
    }

    var timer = null;
    var direction = 1;

    /**
     * Permanent desktop/manual stop.
     */
    var stopped = false;

    /**
     * Temporary states.
     */
    var hovered = false;
    var focused = false;
    var onScreen = false;
    var touchPaused = false;

    /**
     * Timer used after mobile interaction.
     */
    var touchResumeTimer = null;

    /**
     * How long mobile should remain still
     * after the finger is released.
     */
    var TOUCH_RESUME_DELAY = 5000;

    /**
     * Find an optional lightbox belonging
     * to this carousel section.
     */
    var host =
      root.closest('section') || document;

    var dialog =
      host.querySelector(
        '[data-carousel-lightbox]'
      );

    function isTouchInteraction(e) {
      return (
        e.pointerType === 'touch' ||
        e.pointerType === 'pen'
      );
    }

    function isLightboxOpen() {
      return Boolean(
        dialog &&
        dialog.open
      );
    }

    /**
     * Autoplay can only run when every
     * temporary/permanent constraint allows it.
     */
    function canRun() {
      return (
        !stopped &&
        !touchPaused &&
        !hovered &&
        !focused &&
        !isLightboxOpen() &&
        onScreen &&
        !document.hidden
      );
    }

    function cancel() {
      if (timer !== null) {
        window.clearTimeout(timer);
        timer = null;
      }
    }

    function clearTouchResumeTimer() {
      if (touchResumeTimer !== null) {
        window.clearTimeout(
          touchResumeTimer
        );

        touchResumeTimer = null;
      }
    }

    /**
     * One timeout at a time.
     *
     * This deliberately does not use setInterval,
     * because paused carousels should receive a
     * fresh complete interval when they resume.
     */
    function sync() {
      cancel();

      if (canRun()) {
        timer =
          window.setTimeout(
            tick,
            interval
          );
      }
    }

    /**
     * Move one photograph.
     *
     * At the last photograph we reverse
     * direction instead of jumping back to 1.
     */
    function tick() {
      var index = getIndex();

      if (
        index >=
        slides.length - 1
      ) {
        direction = -1;
      } else if (index <= 0) {
        direction = 1;
      }

      goToIndex(
        index + direction
      );

      sync();
    }

    function setToggleState() {
      if (!toggle) return;

      var playing = !stopped;

      var pauseIcon =
        toggle.querySelector(
          '[data-carousel-toggle-pause]'
        );

      var playIcon =
        toggle.querySelector(
          '[data-carousel-toggle-play]'
        );

      if (pauseIcon) {
        pauseIcon.hidden =
          !playing;
      }

      if (playIcon) {
        playIcon.hidden =
          playing;
      }

      var label =
        playing
          ? toggle.getAttribute(
              'data-label-pause'
            )
          : toggle.getAttribute(
              'data-label-play'
            );

      if (label) {
        toggle.setAttribute(
          'aria-label',
          label
        );
      }
    }

    /**
     * Permanent stop.
     *
     * Desktop/manual interaction keeps the
     * existing "user has taken control" behaviour.
     */
    function stop() {
      if (stopped) return;

      stopped = true;

      touchPaused = false;

      clearTouchResumeTimer();
      cancel();

      setToggleState();
    }

    /**
     * MOBILE:
     *
     * As soon as the visitor touches the carousel,
     * stop any currently scheduled automatic move.
     */
    function beginTouchPause() {
      clearTouchResumeTimer();

      touchPaused = true;

      cancel();
    }

    /**
     * MOBILE:
     *
     * After the visitor releases the carousel,
     * keep the photograph still for 5 seconds.
     *
     * Once those 5 seconds finish, normal autoplay
     * gets a completely fresh interval.
     *
     * With the current 4-second carousel interval:
     *
     * touch ends
     * -> 5 seconds quiet
     * -> 4-second normal autoplay interval
     * -> next photograph
     */
    function endTouchPause() {
      clearTouchResumeTimer();

      touchResumeTimer =
        window.setTimeout(
          function () {
            touchResumeTimer = null;

            touchPaused = false;

            sync();
          },
          TOUCH_RESUME_DELAY
        );
    }

    /**
     * Desktop hover.
     */
    root.addEventListener(
      'mouseenter',
      function () {
        hovered = true;

        sync();
      }
    );

    root.addEventListener(
      'mouseleave',
      function () {
        hovered = false;

        sync();
      }
    );

    /**
     * Keyboard focus should pause autoplay.
     *
     * We deliberately ignore this state on
     * coarse/touch devices because tapping a
     * <button> can leave it focused after the tap.
     * That would otherwise prevent mobile autoplay
     * from ever restarting.
     */
    root.addEventListener(
      'focusin',
      function () {
        if (isCoarsePointer()) {
          return;
        }

        focused = true;

        sync();
      }
    );

    root.addEventListener(
      'focusout',
      function () {
        if (isCoarsePointer()) {
          return;
        }

        focused = false;

        sync();
      }
    );

    /**
     * Don't advance in a background tab.
     */
    document.addEventListener(
      'visibilitychange',
      sync
    );

    /**
     * Desktop/manual controls remain permanent
     * takeover actions.
     */
    if (prevBtn) {
      prevBtn.addEventListener(
        'click',
        stop
      );
    }

    if (nextBtn) {
      nextBtn.addEventListener(
        'click',
        stop
      );
    }

    viewport.addEventListener(
      'keydown',
      stop
    );

    /**
     * Pointer behaviour.
     *
     * Touch:
     * temporary pause.
     *
     * Mouse:
     * permanent manual takeover.
     */
    viewport.addEventListener(
      'pointerdown',
      function (e) {
        if (
          isTouchInteraction(e)
        ) {
          beginTouchPause();

          return;
        }

        if (
          e.pointerType === 'mouse'
        ) {
          stop();
        }
      }
    );

    viewport.addEventListener(
      'pointerup',
      function (e) {
        if (
          !isTouchInteraction(e)
        ) {
          return;
        }

        endTouchPause();
      }
    );

    viewport.addEventListener(
      'pointercancel',
      function (e) {
        if (
          !isTouchInteraction(e)
        ) {
          return;
        }

        endTouchPause();
      }
    );

    /**
     * Opening the lightbox should stop the
     * background carousel from advancing.
     *
     * Closing it returns mobile autoplay to
     * normal behaviour.
     */
    if (dialog) {
      dialog.addEventListener(
        'close',
        function () {
          clearTouchResumeTimer();

          touchPaused = false;

          sync();
        }
      );
    }

    /**
     * Explicit desktop Play/Pause control.
     */
    if (toggle) {
      toggle.addEventListener(
        'click',
        function () {
          if (stopped) {
            stopped = false;
            touchPaused = false;

            clearTouchResumeTimer();

            setToggleState();
            sync();
          } else {
            stop();
          }
        }
      );

      setToggleState();
    }

    /**
     * Only autoplay while carousel itself
     * is meaningfully visible.
     */
    if (
      'IntersectionObserver' in window
    ) {
      new IntersectionObserver(
        function (entries) {
          entries.forEach(
            function (entry) {
              onScreen =
                entry.isIntersecting;
            }
          );

          sync();
        },
        {
          threshold: [0.35]
        }
      ).observe(root);
    } else {
      onScreen = true;

      sync();
    }
  }

  /**
   * ---------------------------------------------------------
   * INITIALISATION
   * ---------------------------------------------------------
   */
  function init() {
    var carousels =
      document.querySelectorAll(
        '[data-silensia-carousel]'
      );

    for (
      var i = 0;
      i < carousels.length;
      i++
    ) {
      initCarousel(
        carousels[i]
      );
    }
  }

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      init
    );
  } else {
    init();
  }

  /**
   * Shopify Theme Editor.
   *
   * Sections are replaced dynamically when their
   * settings change, so initialise freshly injected
   * carousel markup.
   */
  document.addEventListener(
    'shopify:section:load',
    function (event) {
      var scope =
        event.target &&
        event.target.querySelectorAll
          ? event.target
          : document;

      var carousels =
        scope.querySelectorAll(
          '[data-silensia-carousel]'
        );

      for (
        var i = 0;
        i < carousels.length;
        i++
      ) {
        initCarousel(
          carousels[i]
        );
      }
    }
  );
})();